import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Building2, MapPin } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { servicesData } from '../mock';

const ServicesPage = () => {
  const iconMap = {
    Home: Home,
    Building2: Building2,
    MapPin: MapPin,
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Professional Appraisal Services
          </h1>
          <p className="text-xl text-sky-50 max-w-3xl mx-auto">
            Comprehensive residential property valuations tailored to your specific needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-metallic-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {servicesData.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-sky-200"
                >
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-sky-600 to-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl group-hover:text-sky-600 transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/services/${service.slug}`}>
                      <Button className="w-full bg-gradient-to-r from-sky-600 to-green-600 hover:from-sky-700 hover:to-green-700 text-white">
                        View Details
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-metallic-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose St. Croix Appraisals?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white border-2 border-sky-100 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Turnaround</h3>
              <p className="text-gray-600">
                Most appraisals completed within 48-72 hours without compromising quality or accuracy.
              </p>
            </div>
            <div className="bg-white border-2 border-sky-100 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Local Expertise</h3>
              <p className="text-gray-600">
                Deep knowledge of Twin Cities metro area and Wisconsin market trends, neighborhoods, and property values.
              </p>
            </div>
            <div className="bg-white border-2 border-sky-100 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">USPAP Compliant</h3>
              <p className="text-gray-600">
                All appraisals meet or exceed Uniform Standards of Professional Appraisal Practice.
              </p>
            </div>
            <div className="bg-white border-2 border-green-100 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Reports</h3>
              <p className="text-gray-600">
                Detailed documentation with photos, property sketches, and market analysis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-sky-600 to-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Schedule Your Appraisal?
          </h2>
          <p className="text-xl text-sky-50 mb-8">
            Contact us today for a professional property valuation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-lg">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;